import { useMemo } from 'react';
import { Configuration, ProductListsApi } from '@/api';
import { ProductListFilters } from '@/api/ProductListsApi';
import { productListQueryKeys } from '@/api/queries/productListQueryKeys';
import { EmptyResult } from '@/models/Pagination';
import { ProductList, ProductLists } from '@/models/ProductList';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const initialState = EmptyResult<ProductList>();
const itemsPerPage = 10;

interface FindProductListsQueryResult {
  productLists: ProductLists;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  itemsPerPage: number;
  invalidateQuery: () => Promise<void>;
}

const useFindProductListsQuery = (
  page: number,
  searchQuery?: string,
  filters?: ProductListFilters,
): FindProductListsQueryResult => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const productListsApi = useMemo(() => new ProductListsApi(new Configuration(auth)), [auth]);
  const queryKey = productListQueryKeys.list(page, searchQuery, filters);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey,
    queryFn: () =>
      productListsApi.findProductLists(
        searchQuery,
        page * itemsPerPage - itemsPerPage,
        itemsPerPage,
        filters,
      ),
  });

  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey });

  return {
    productLists: data ?? initialState,
    isLoading,
    isFetching,
    error,
    itemsPerPage,
    invalidateQuery,
  };
};

export default useFindProductListsQuery;
